/**
 * Admin Overview Page
 *
 * Dashboard with key metrics and quick actions.
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, query, where, getDocs, orderBy, limit, Timestamp } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { useAdminAuth } from '@/contexts/AdminAuthContext';
import { AdminPageHeader, AdminCard, AdminStatCard } from '@/components/admin/AdminShell';
import { CreateOrgModal } from '@/components/admin/CreateOrgModal';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Building2,
  Users,
  ClipboardCheck,
  AlertTriangle,
  Search,
  Plus,
  ArrowRight,
  TrendingUp,
  Clock,
} from 'lucide-react';
import { Org, ComplianceRunSummary } from '@/integrations/firebase/types';

interface DashboardStats {
  totalOrgs: number;
  newOrgsLast7Days: number;
  newOrgsLast30Days: number;
  activeOrgs: number;
  totalRuns: number;
  failedRuns: number;
  freeChecksStarted: number;
  freeChecksCompleted: number;
}

export default function AdminOverview() {
  const navigate = useNavigate();
  const { adminUser, isSuperadmin } = useAdminAuth();
  const [searchQuery, setSearchQuery] = useState('');
  const [createOrgModalOpen, setCreateOrgModalOpen] = useState(false);
  const [stats, setStats] = useState<DashboardStats>({
    totalOrgs: 0,
    newOrgsLast7Days: 0,
    newOrgsLast30Days: 0,
    activeOrgs: 0,
    totalRuns: 0,
    failedRuns: 0,
    freeChecksStarted: 0,
    freeChecksCompleted: 0,
  });
  const [recentOrgs, setRecentOrgs] = useState<Org[]>([]);
  const [recentRuns, setRecentRuns] = useState<ComplianceRunSummary[]>([]);
  const [loading, setLoading] = useState(true);

  // Fetch dashboard data
  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setLoading(true);

        // Get date thresholds
        const now = new Date();
        const sevenDaysAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
        const thirtyDaysAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);

        // Fetch orgs
        const orgsSnapshot = await getDocs(collection(db, 'orgs'));
        const orgs = orgsSnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        })) as Org[];

        const totalOrgs = orgs.length;
        const activeOrgs = orgs.filter((o) => o.status === 'active').length;
        const newOrgsLast7Days = orgs.filter(
          (o) => o.createdAt?.toDate() >= sevenDaysAgo
        ).length;
        const newOrgsLast30Days = orgs.filter(
          (o) => o.createdAt?.toDate() >= thirtyDaysAgo
        ).length;

        // Recent orgs (last 5)
        const sortedOrgs = [...orgs].sort(
          (a, b) => (b.createdAt?.toMillis() || 0) - (a.createdAt?.toMillis() || 0)
        );
        setRecentOrgs(sortedOrgs.slice(0, 5));

        // Fetch compliance runs (stub - would need actual collection)
        // For now, use placeholder data
        const totalRuns = 0;
        const failedRuns = 0;

        setStats({
          totalOrgs,
          newOrgsLast7Days,
          newOrgsLast30Days,
          activeOrgs,
          totalRuns,
          failedRuns,
          freeChecksStarted: 0,
          freeChecksCompleted: 0,
        });
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, [createOrgModalOpen]); // Re-fetch when modal closes (after creating org)

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/admin/orgs?search=${encodeURIComponent(searchQuery.trim())}`);
    }
  };

  const handleOrgCreated = (orgId: string) => {
    setCreateOrgModalOpen(false);
    navigate(`/admin/orgs/${orgId}`);
  };

  return (
    <div>
      <AdminPageHeader
        title="Admin Overview"
        description={`Welcome back, ${adminUser?.displayName || adminUser?.email}`}
        actions={
          <Button
            onClick={() => setCreateOrgModalOpen(true)}
            className="bg-[hsl(220,70%,50%)] hover:bg-[hsl(220,70%,45%)]"
          >
            <Plus className="h-4 w-4 mr-2" />
            New Organization
          </Button>
        }
      />

      {/* Create Organization Modal */}
      <CreateOrgModal
        open={createOrgModalOpen}
        onOpenChange={setCreateOrgModalOpen}
        onSuccess={handleOrgCreated}
      />

      {/* Global Search */}
      <form onSubmit={handleSearch} className="mb-6">
        <div className="relative max-w-xl">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
          <Input
            type="text"
            placeholder="Search orgs, users, jobs, or runs..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 bg-[hsl(220,20%,12%)] border-[hsl(220,15%,25%)] text-white placeholder:text-gray-500"
          />
        </div>
      </form>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <AdminStatCard
          label="Total Organizations"
          value={stats.totalOrgs}
          change={`${stats.activeOrgs} active`}
          changeType="neutral"
          icon={<Building2 className="h-5 w-5" />}
        />
        <AdminStatCard
          label="New Orgs (7d)"
          value={stats.newOrgsLast7Days}
          change={`${stats.newOrgsLast30Days} in 30d`}
          changeType={stats.newOrgsLast7Days > 0 ? 'positive' : 'neutral'}
          icon={<TrendingUp className="h-5 w-5" />}
        />
        <AdminStatCard
          label="Compliance Runs"
          value={stats.totalRuns || '—'}
          change={stats.failedRuns > 0 ? `${stats.failedRuns} failed` : 'No data yet'}
          changeType={stats.failedRuns > 0 ? 'negative' : 'neutral'}
          icon={<ClipboardCheck className="h-5 w-5" />}
        />
        <AdminStatCard
          label="Free Checks"
          value={`${stats.freeChecksCompleted}/${stats.freeChecksStarted || '—'}`}
          change="Conversion rate"
          changeType="neutral"
          icon={<Users className="h-5 w-5" />}
        />
      </div>

      {/* Two Column Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Organizations */}
        <AdminCard>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-medium text-white">Recent Organizations</h2>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/admin/orgs')}
              className="text-gray-400 hover:text-white"
            >
              View all
              <ArrowRight className="h-4 w-4 ml-1" />
            </Button>
          </div>

          {loading ? (
            <div className="text-center py-8 text-gray-500">Loading...</div>
          ) : recentOrgs.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              No organizations yet
            </div>
          ) : (
            <div className="space-y-3">
              {recentOrgs.filter(org => org.name).map((org) => (
                <button
                  key={org.id}
                  onClick={() => navigate(`/admin/orgs/${org.id}`)}
                  className="w-full flex items-center justify-between p-3 rounded-lg bg-[hsl(220,15%,12%)] hover:bg-[hsl(220,15%,15%)] transition-colors text-left"
                >
                  <div className="flex items-center gap-3">
                    <div className="h-9 w-9 rounded-full bg-[hsl(220,15%,20%)] flex items-center justify-center">
                      <Building2 className="h-4 w-4 text-gray-400" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-white">{org.name}</p>
                      <p className="text-xs text-gray-500">{org.primaryContactEmail || 'No email'}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge
                      variant="outline"
                      className={
                        org.status === 'active'
                          ? 'border-green-500/50 text-green-400'
                          : 'border-red-500/50 text-red-400'
                      }
                    >
                      {org.status}
                    </Badge>
                    <ArrowRight className="h-4 w-4 text-gray-500" />
                  </div>
                </button>
              ))}
            </div>
          )}
        </AdminCard>

        {/* Quick Actions / System Status */}
        <AdminCard>
          <h2 className="text-lg font-medium text-white mb-4">Quick Actions</h2>

          <div className="space-y-3">
            <button
              onClick={() => setCreateOrgModalOpen(true)}
              className="w-full flex items-center gap-3 p-3 rounded-lg bg-[hsl(220,15%,12%)] hover:bg-[hsl(220,15%,15%)] transition-colors text-left"
            >
              <div className="h-9 w-9 rounded-md bg-blue-500/20 flex items-center justify-center">
                <Plus className="h-4 w-4 text-blue-400" />
              </div>
              <div>
                <p className="text-sm font-medium text-white">Create Organization</p>
                <p className="text-xs text-gray-500">Onboard a new customer</p>
              </div>
            </button>

            <button
              onClick={() => navigate('/admin/runs')}
              className="w-full flex items-center gap-3 p-3 rounded-lg bg-[hsl(220,15%,12%)] hover:bg-[hsl(220,15%,15%)] transition-colors text-left"
            >
              <div className="h-9 w-9 rounded-md bg-purple-500/20 flex items-center justify-center">
                <ClipboardCheck className="h-4 w-4 text-purple-400" />
              </div>
              <div>
                <p className="text-sm font-medium text-white">Search Compliance Runs</p>
                <p className="text-xs text-gray-500">Find runs across all orgs</p>
              </div>
            </button>

            {isSuperadmin && (
              <button
                onClick={() => navigate('/admin/audit')}
                className="w-full flex items-center gap-3 p-3 rounded-lg bg-[hsl(220,15%,12%)] hover:bg-[hsl(220,15%,15%)] transition-colors text-left"
              >
                <div className="h-9 w-9 rounded-md bg-amber-500/20 flex items-center justify-center">
                  <Clock className="h-4 w-4 text-amber-400" />
                </div>
                <div>
                  <p className="text-sm font-medium text-white">View Audit Log</p>
                  <p className="text-xs text-gray-500">Review admin activity</p>
                </div>
              </button>
            )}
          </div>

          {/* System Status */}
          <div className="mt-6 pt-4 border-t border-[hsl(220,15%,20%)]">
            <h3 className="text-sm font-medium text-gray-400 mb-3">System Status</h3>
            <div className="flex items-center gap-2">
              <div className="h-2 w-2 rounded-full bg-green-500" />
              <span className="text-sm text-gray-300">All systems operational</span>
            </div>
          </div>
        </AdminCard>
      </div>
    </div>
  );
}
